import showToast from '../../Components/Common/SweetAlert/ToastAlert'
import { fetchAssignedTaskDetails, updateTask } from '../../redux/task/actions'
import { useAppDispatch, useAppSelector, type AppDispatch } from '../../redux/store'
import { selectUserTeamMemberInfo } from '../../redux/auth/selector'
import { useLoading } from '../../context/LoadingContext'

interface UpdateTaskPayload {
  id: number
  title: string
  dueDate: Date | null
  details: string
  link: string
  importance: string
  operationalManager: string | string[]
  functionalArea: string | string[]
  supervisor: string | string[]
  // New fields to preserve original user selections
  originalOperationalManagerSelection?: string[]
  originalFunctionalAreaSelection?: string[]
  originalSupervisorSelection?: string[]
  status: string
  createdBy: string
  required?: boolean
  teamMembers?: any[]
}

const useUpdateTask = () => {
  const dispatch: AppDispatch = useAppDispatch()
  const { setLoading } = useLoading()

  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const teamMemberDetailsPayload = {
    teamMemberEmail: teamMemberDetails.email,
    teamMemberWorkdayId: teamMemberDetails.workdayId,
  }

  const handleUpdateTask = async (payload: UpdateTaskPayload) => {
    setLoading(true)
    try {
      // Convert operationalManager workdayId(s) to OperationalManager object(s)
      const operationalManagerArray: any[] = []
      if (payload.operationalManager && payload.teamMembers && payload.teamMembers.length > 0) {
        const operationalManagerIds = Array.isArray(payload.operationalManager) ? payload.operationalManager : [payload.operationalManager]
        operationalManagerIds.forEach(omId => {
          const teamMember = payload.teamMembers!.find((member: any) => 
            member.operationalManager && member.operationalManager.workdayId === omId
          )
          if (teamMember?.operationalManager) {
            operationalManagerArray.push(teamMember.operationalManager)
          }
        })
      }

      // Convert functionalArea to array if it's a string
      const functionalAreaArray = Array.isArray(payload.functionalArea) ? payload.functionalArea : [payload.functionalArea]
      
      // Convert supervisor workdayId(s) to Supervisor object(s)
      const supervisorArray: any[] = []
      if (payload.supervisor && payload.teamMembers && payload.teamMembers.length > 0) {
        const supervisorIds = Array.isArray(payload.supervisor) ? payload.supervisor : [payload.supervisor]
        supervisorIds.forEach(sId => {
          const teamMember = payload.teamMembers!.find((member: any) => 
            member.supervisor && member.supervisor.workdayId === sId
          )
          if (teamMember?.supervisor) {
            supervisorArray.push(teamMember.supervisor)
          }
        })
      }

      // Prepare the update payload - preserve status and dueDate from original task
      const updatePayload = {
        id: payload.id,
        title: payload.title,
        details: payload.details,
        link: payload.link,
        importance: payload.importance,
        operationalManager: operationalManagerArray.length > 0 ? operationalManagerArray : undefined,
        functionalArea: functionalAreaArray,
        supervisor: supervisorArray,
        originalOperationalManagerSelection: payload.originalOperationalManagerSelection,
        originalFunctionalAreaSelection: payload.originalFunctionalAreaSelection,
        originalSupervisorSelection: payload.originalSupervisorSelection,
        // Include these fields to satisfy TypeScript, but they will be ignored by the backend
        dueDate: payload.dueDate,
        status: payload.status,
        createdBy: payload.createdBy,
        required: payload.required,
        // Add a flag to tell the backend not to update assigned date and status
        preserveAssignedDateAndStatus: true
      }

      console.log('Update payload:', updatePayload)

      await dispatch(updateTask(updatePayload))
      showToast('success', 'Task updated successfully!')
    } catch (error) {
      showToast('error', 'Error updating task')
      console.error('Error updating task:', error)
    } finally {
      setLoading(false)
      await dispatch(fetchAssignedTaskDetails(teamMemberDetailsPayload)).unwrap()
    }
  }

  return {
    handleUpdateTask: handleUpdateTask,
  }
}

export default useUpdateTask
